import {Box, Button, Checkbox, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography} from "@mui/material";
import dayjs from "dayjs";
import {Order} from "../../types/order";

interface ReportsTableProps{
    orders: Order[];
    selectedOrderIds: number[];
    onToggleOrderId: (idOrder: number) => void;
    onToggleAllVisible: () => void;
    onClearSelection: () => void;
    isEditingOrderNumbers: boolean;
    orderNumberDrafts: Record<number, string>;
    onChangeOrderNumberDraft: (idOrder: number, nextValue: string) => void;
}

export const ReportsTable: React.FC<ReportsTableProps> = (props) => {
    const visibleOrderIds = props.orders
        .map((o) => o.idOrder)
        .filter((id): id is number => typeof id === 'number');

    const allSelected = visibleOrderIds.length > 0 && visibleOrderIds.every((id) => props.selectedOrderIds.includes(id));
    const someSelected = visibleOrderIds.some((id) => props.selectedOrderIds.includes(id)) && !allSelected;

    return(
        <TableContainer sx={{ overflowX: 'auto' }}>
            <Box sx={{minWidth: 800}}>
                <Table>
                    <TableHead sx={{background: '#f8f9fa'}}>
                        <TableRow>
                            <TableCell padding="checkbox" sx={{ whiteSpace: 'nowrap' }}>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Checkbox
                                        indeterminate={someSelected}
                                        checked={allSelected}
                                        onChange={props.onToggleAllVisible}
                                        inputProps={{ 'aria-label': 'select all orders' }}
                                    />
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={props.onClearSelection}
                                        sx={{ textTransform: 'none', minWidth: 'auto', px: 0.75, fontSize: '0.8125rem' }}
                                    >
                                        Pastro
                                    </Button>
                                </Stack>
                            </TableCell>
                            <TableCell>
                                Nr
                            </TableCell>
                            <TableCell>
                                Data e porosisë
                            </TableCell>
                            <TableCell>
                                Klienti
                            </TableCell>
                            <TableCell>
                                Lloji
                            </TableCell>
                            <TableCell>
                                Sasia (kg)
                            </TableCell>
                            <TableCell>
                                Çmimi
                            </TableCell>
                            <TableCell>
                                Totali
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {props.orders.map((order) => {
                            const formattedMonth = order.orderDate ? dayjs(order.orderDate).format('MMM') : '';
                            const formattedDay = order.orderDate ? dayjs(order.orderDate).format('DD') : '';
                            const idOrder = order.idOrder;
                            const isSelected = typeof idOrder === 'number' && props.selectedOrderIds.includes(idOrder);

                            return (
                                <TableRow
                                    hover
                                    key={order.idOrder ?? `${order.orderNumber}-${formattedDay}-${formattedMonth}`}
                                    selected={isSelected}
                                >
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={isSelected}
                                            disabled={typeof idOrder !== 'number'}
                                            onChange={() => typeof idOrder === 'number' && props.onToggleOrderId(idOrder)}
                                            inputProps={{ 'aria-label': `select order ${order.idOrder}` }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {props.isEditingOrderNumbers && isSelected && typeof idOrder === 'number'
                                            ? (
                                                <TextField
                                                    size="small"
                                                    value={props.orderNumberDrafts[idOrder] ?? String(order.orderNumber ?? '')}
                                                    onChange={(e) => props.onChangeOrderNumberDraft(idOrder, e.target.value)}
                                                    type="text"
                                                    sx={{ width: 120 }}
                                                />
                                            )
                                            : order.orderNumber
                                        }
                                    </TableCell>
                                    <TableCell>
                                        <Box
                                            sx={{
                                                p: '5px 10px',
                                                backgroundColor: '#e7eaed',
                                                borderRadius: 2,
                                                maxWidth: 'fit-content',
                                            }}
                                        >
                                            <Typography
                                                align="center"
                                                color="text.primary"
                                                variant="caption"
                                                sx={{textTransform: 'uppercase', fontSize: '15px'}}
                                            >
                                                {formattedMonth}
                                            </Typography>
                                            <Typography
                                                align="center"
                                                color="text.primary"
                                                variant="h6"
                                            >
                                                {formattedDay}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {`${order.idClient?.firstName} ${order.idClient?.lastName}`}
                                    </TableCell>
                                    <TableCell>
                                        {order.type}
                                    </TableCell>
                                    <TableCell>
                                        {order.quantity_per_kg}kg
                                    </TableCell>
                                    <TableCell>
                                        {order.price}&euro;
                                    </TableCell>
                                    <TableCell>
                                        {order.totalPrice}&euro;
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </Box>
        </TableContainer>
    )
}